import React, { useEffect } from 'react'
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { Alert, FormInput, GradientButton, Heading } from '@/components'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { navigate, useLocation } from '@reach/router'
import { useAccountActivation, useGetActivationInfo } from '@/hooks'

const useStyles = makeStyles((theme) => ({
  alert: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },

  button: {},

  container: {
    backgroundColor: '#fff',
    borderRadius: '15px',
    padding: theme.spacing(2),
  },

  explanation: {
    textAlign: 'center',
  },

  explanationText: {
    fontSize: '1rem',
    margin: '0.25rem',
  },

  input: {
    marginBottom: '2rem',
  },

  invalidToken: {
    textAlign: 'center',
    marginTop: theme.spacing(4),
  },

  loginButton: {
    marginTop: theme.spacing(2),
  },

  root: {
    margin: '2rem 25%',
    [theme.breakpoints.down('sm')]: {
      margin: '0.25rem 0.2rem',
    },
  },
}))

const ActivateAccount = (
  {
    /* props */
  }
) => {
  const classes = useStyles()
  const { t } = useTranslation('activation')
  const [succeeded, setSucceeded] = React.useState(false)

  const useQuery = (queryParam) => {
    const search = new URLSearchParams(useLocation().search)
    return search.get(queryParam)
  }

  const token = useQuery('token')

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
  })

  const { mutate: activateAccount, isPending, error } = useAccountActivation()
  const {
    data: userInfo,
    isLoading: isCheckingToken,
    error: infoError,
  } = useGetActivationInfo(token)

  const password = watch('password')

  const onSubmit = (data) => {
    activateAccount(
      {
        token,
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
      },
      {
        onSuccess: () => {
          setSucceeded(true)
        },
      }
    )
  }

  const hasValidToken = Boolean(token)

  const handleNavigateToLogin = () => {
    navigate('/login', { replace: true })
  }

  const handleNavigateToServices = () => {
    navigate('/patient/services', { replace: true })
  }

  if (!hasValidToken) {
    return (
      <>
        <Container className={classes.container}>
          <Grid className={classes.root}>
            <Heading>{t('header')}</Heading>
            <div className={classes.invalidToken}>
              <Typography component="h3" className={classes.explanationText}>
                {t('invalidToken')}
              </Typography>
            </div>
          </Grid>
        </Container>
      </>
    )
  }

  if (isCheckingToken) {
    return (
      <Container
        className={classes.container}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Grid className={classes.root}>
          <Heading>{t('header')}</Heading>
          <Box display="flex" justifyContent="center" mb={2}>
            <CircularProgress title={t('checkingToken')} />
          </Box>

          <div className={classes.explanation}>
            <Typography component="h3" className={classes.explanationText}>
              {t('checkingToken')}
            </Typography>
          </div>
        </Grid>
      </Container>
    )
  }

  if (infoError) {
    return (
      <Container
        className={classes.container}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Grid className={classes.root}>
          <Heading>{t('header')}</Heading>

          <Alert className={classes.alert} severity="error">
            <span
              dangerouslySetInnerHTML={{
                __html: infoError.response?.data?.detail,
              }}
            ></span>
          </Alert>
        </Grid>
      </Container>
    )
  }

  if (userInfo?.isAlreadyActive) {
    return (
      <Container className={classes.container}>
        <Grid className={classes.root}>
          <Heading>{t('header')}</Heading>
          <div className={classes.invalidToken}>
            <Typography component="h3" className={classes.explanationText}>
              {t('welcome').replace('{firstName}', userInfo?.firstName || '')}
            </Typography>

            <Typography
              component="h3"
              className={classes.explanationText}
              mb={2}
            >
              {t('accountAlreadyActivated')}
            </Typography>

            <GradientButton
              type="button"
              className={classes.loginButton}
              onClick={handleNavigateToLogin}
            >
              {t('loginButton')}
            </GradientButton>
          </div>
        </Grid>
      </Container>
    )
  }

  return (
    <>
      <Container className={classes.container}>
        <Grid className={classes.root}>
          <Heading>{t('header')}</Heading>

          <div className={classes.explanation}>
            {!succeeded && (
              <Typography component="h3" className={classes.explanationText}>
                {t('explanation').replace(
                  '{firstName}',
                  userInfo?.firstName || ''
                )}
              </Typography>
            )}

            {succeeded && (
              <Typography component="h3" className={classes.explanationText}>
                {t('activationSuccess').replace(
                  '{firstName}',
                  userInfo?.firstName || ''
                )}
              </Typography>
            )}
          </div>

          {error && (
            <Alert className={classes.alert} severity="error">
              <span
                dangerouslySetInnerHTML={{
                  __html: error || error.response?.data?.detail,
                }}
              ></span>
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {!succeeded && (
              <>
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: t('passwordRequired'),
                    minLength: {
                      value: 5,
                      message: t('passwordMinLength'),
                    },
                  }}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      type="password"
                      label={t('passwordLabel')}
                      placeholder={t('passwordPlaceholder')}
                      className={classes.input}
                      disabled={isPending}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      error={!!errors.password}
                      errorMessage={errors?.password?.message ?? ''}
                      autocomplete="new-password"
                    />
                  )}
                />

                <Controller
                  name="passwordConfirmation"
                  control={control}
                  rules={{
                    required: t('passwordConfirmationRequired'),
                    validate: (value) =>
                      value === password || t('passwordMismatch'),
                  }}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      type="password"
                      label={t('passwordConfirmationLabel')}
                      placeholder={t('passwordConfirmationPlaceholder')}
                      className={classes.input}
                      disabled={isPending}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      error={!!errors.passwordConfirmation}
                      errorMessage={errors?.passwordConfirmation?.message ?? ''}
                      autocomplete="new-password"
                    />
                  )}
                />
              </>
            )}

            {isPending && (
              <Box display="flex" justifyContent="center" mb={2}>
                <CircularProgress title={t('activatingAccount')} />
              </Box>
            )}

            {!succeeded && (
              <GradientButton
                type="submit"
                className={classes.button}
                disabled={isPending}
              >
                {isPending ? t('activatingAccount') : t('submitButton')}
              </GradientButton>
            )}

            {succeeded && (
              <>
                <GradientButton
                  type="button"
                  className={classes.loginButton}
                  onClick={handleNavigateToServices}
                >
                  {t('continueToServices')}
                </GradientButton>
              </>
            )}
          </form>
        </Grid>
      </Container>
    </>
  )
}

ActivateAccount.displayName = 'ActivateAccount'
export { ActivateAccount }
